import React, { useEffect, useState } from 'react'
import { collectionsApi } from '../../services/collectionsApi'
import type { CollectionWithItems, CollectionItem } from '../../types/collections'
import { useCollectionItem } from '../../hooks/useCollectionItem'
import EditCollectionItemModal from './EditCollectionItemModal'
import EditPlaceById from './EditPlaceById'

interface Props {
  collectionId: number
  itemId: string | number
  onClose: () => void
  onSuccess?: () => void
}

/**
 * Single entry point for editing a collection item when the caller only has
 * the ids. Dispatches by the target collection's `system_kind` so each kind
 * gets its purpose-built editor — Places opens the map+address picker
 * (`LocationPickerModal`), everything else falls through to the generic
 * `EditCollectionItemModal`. People intentionally falls through (no custom
 * editor today); add a branch here if that changes.
 */
const EditCollectionItemById: React.FC<Props> = ({
  collectionId,
  itemId,
  onClose,
  onSuccess,
}) => {
  const [collection, setCollection] = useState<CollectionWithItems | null>(null)
  const { item } = useCollectionItem(collectionId, itemId)

  useEffect(() => {
    let cancelled = false
    collectionsApi.get(collectionId).then((data) => {
      if (!cancelled) setCollection(data)
    }).catch((err) => {
      console.error('Failed to load collection for edit:', err)
    })
    return () => { cancelled = true }
  }, [collectionId])

  if (!collection) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  if (collection.system_kind === 'places') {
    return (
      <EditPlaceById
        collectionId={collectionId}
        itemId={Number(itemId)}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    )
  }

  if (!item) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  return (
    <EditCollectionItemModal
      collection={collection}
      item={item as CollectionItem}
      onClose={onClose}
      onSuccess={() => {
        onSuccess?.()
      }}
    />
  )
}

export default EditCollectionItemById
